type DocumentLike = {
  documentGuid?: string | null;
  documentId?: number | string | null;
  fileName?: string | null;
  url?: string | null;
  isDeleted?: boolean | null;
  isPrimary?: boolean | null;
  sequenceNumber?: number | null;
};

export const MEDIA_KIND = {
  IMAGE: 'image',
  VIDEO: 'video',
} as const;

export type MediaKind = (typeof MEDIA_KIND)[keyof typeof MEDIA_KIND];

const IMAGE_EXTENSIONS = new Set([
  'apng',
  'avif',
  'bmp',
  'gif',
  'jpeg',
  'jpg',
  'png',
  'svg',
  'webp',
]);

const VIDEO_EXTENSIONS = new Set([
  'avi',
  'm4v',
  'mov',
  'mp4',
  'mpeg',
  'mpg',
  'webm',
]);

const INLINE_VIDEO_CONTENT_TYPES = new Set([
  'video/mp4',
  'video/mpeg',
  'video/quicktime',
  'video/webm',
  'video/x-msvideo',
]);

export function isInlineVideoContentType(
  contentType: string | null | undefined,
): boolean {
  const normalized = contentType?.split(';', 1)[0]?.trim().toLowerCase();
  return Boolean(normalized && INLINE_VIDEO_CONTENT_TYPES.has(normalized));
}

function getExtension(value: string | null | undefined) {
  if (!value) return null;

  try {
    const parsedUrl = new URL(value);
    const path = parsedUrl.pathname.split('/').pop() ?? '';
    const ext = path.split('.').pop()?.toLowerCase();
    return ext && ext !== path.toLowerCase() ? ext : null;
  } catch {
    const ext = value.split('.').pop()?.toLowerCase();
    return ext && ext !== value.toLowerCase() ? ext : null;
  }
}

export function getDocumentMediaKind(
  document: DocumentLike | null | undefined,
): MediaKind | null {
  const ext = getExtension(document?.fileName) ?? getExtension(document?.url);
  if (!ext) return null;
  if (IMAGE_EXTENSIONS.has(ext)) return MEDIA_KIND.IMAGE;
  if (VIDEO_EXTENSIONS.has(ext)) return MEDIA_KIND.VIDEO;
  return null;
}

export function isImageDocument(document: DocumentLike | null | undefined) {
  return getDocumentMediaKind(document) === MEDIA_KIND.IMAGE;
}

export function getDocumentIdentity(
  document: DocumentLike | null | undefined,
): string | null {
  if (!document) return null;
  if (document.documentGuid) return `guid:${document.documentGuid}`;
  if (document.documentId != null) return `id:${document.documentId}`;
  if (document.url) return `url:${document.url}`;
  if (document.fileName) return `name:${document.fileName}`;
  return null;
}

export function buildDocumentProxyUrl(
  url: string | null | undefined,
  societyId: string | number | null | undefined,
) {
  if (!url || societyId == null || societyId === '') return null;
  const params = new URLSearchParams({
    url,
    societyId: String(societyId),
  });
  return `/api/document?${params.toString()}`;
}

/** Returns the first document whose filename contains "logo" (case-insensitive), or undefined. */
export function findLogoDocument<T extends Pick<DocumentLike, 'fileName'>>(
  documents: T[] | null | undefined,
): T | undefined {
  return documents?.find((d) => d.fileName?.toLowerCase().includes('logo'));
}

export function compareMediaDocuments(a: DocumentLike, b: DocumentLike) {
  const primaryDiff =
    Number(Boolean(b.isPrimary)) - Number(Boolean(a.isPrimary));
  if (primaryDiff !== 0) return primaryDiff;

  const sequenceA = a.sequenceNumber ?? Number.MAX_SAFE_INTEGER;
  const sequenceB = b.sequenceNumber ?? Number.MAX_SAFE_INTEGER;
  if (sequenceA !== sequenceB) return sequenceA - sequenceB;

  return (a.fileName ?? '').localeCompare(b.fileName ?? '');
}

function getFileNameBase(fileName: string | null | undefined): string | null {
  if (!fileName) return null;
  const trimmed = fileName.trim();
  if (!trimmed) return null;
  const lastDot = trimmed.lastIndexOf('.');
  return lastDot > 0 ? trimmed.slice(0, lastDot) : trimmed;
}

/** True when filename follows the `{prefix}_L.*` large-image convention. */
export function isLargeImageFileName(
  fileName: string | null | undefined,
): boolean {
  const base = getFileNameBase(fileName);
  return Boolean(base?.toLowerCase().endsWith('_l'));
}

// Filenames follow `{prefix}.*` (thumbnail) and `{prefix}_L.*` (large image)
// conventions. When a large image exists for a given prefix, the bare
// thumbnail is redundant and should be hidden from the media tab.
export function filterRedundantThumbnails<
  T extends { fileName?: string | null },
>(items: T[]): T[] {
  const prefixesWithLarge = new Set<string>();
  items.forEach((item) => {
    const base = getFileNameBase(item.fileName);
    if (!base || !isLargeImageFileName(item.fileName)) return;
    prefixesWithLarge.add(base.slice(0, -2).toLowerCase());
  });

  if (prefixesWithLarge.size === 0) return items;

  return items.filter((item) => {
    const base = getFileNameBase(item.fileName);
    if (!base) return true;
    return !prefixesWithLarge.has(base.toLowerCase());
  });
}
